import { TableName } from '../../../../../tables'
import { HoardOrder, HoardType, SaleOperation, SaleOrder } from '../../../state/types'
import { useEffect, useMemo, useState } from 'react'
import { fileNameFromUrl, DownloadFilesModal, FileOption, fileHelpers } from '../../../../../features/files'
import { ModalId, useUIState } from '../../../../../features/ui'
import { SalePDF } from '../../sale/sale-pdf/salePDF'
import { HoardPDF } from '../../hoard/hoard-pdf/hoardPDF'
import { PaymentStatus } from '../../../../operations'
import { usePaymentTypeLoaders, PaymentReceiptPDF, CreditPayment, useCreditPaymentLoaders, PaymentCategory, AdjustmentNote, useAdjustmentNoteLoaders, AdjustmentNotePDF } from '../../../../administration'
import { useHoardHooks } from '../../../hooks/hoardHooks'
import { useDownloadHoardPricesListCSV } from '../../../../../features/csv-downloader'
import { HoardStatusPDF } from '../../hoard/hoard-status/hoardStatusPDF'
import { FiscalDocument, fiscalDocumentHelpers, FiscalDocumentStatus, useFiscalDocumentLoaders } from '../../../../fiscal-documents'
import { useDataTypesHooks, useDescribeHooks } from '../../../../../features/data-types'
import { DeliveryNotesFileOptions, Delivery, DeliveryOrder, useDeliveryLoaders, useDeliveryOrderLoaders } from '../../../../deliveries'

type Props = {
    table: TableName
    saleOperation?: SaleOperation
}

const shouldHideAdjustmentNote = (adjustmentNote: AdjustmentNote) =>
    !!adjustmentNote.fiscalDocumentId &&
    (adjustmentNote.fiscalStatus === FiscalDocumentStatus.AUTHORIZED ||
        adjustmentNote.fiscalStatus === FiscalDocumentStatus.TO_AUTHORIZE)

export const DownloadSaleFilesModal = (props: Props) => {
    const { table, saleOperation } = props

    const [deliveries, setDeliveries] = useState<Delivery[]>([])
    const [deliveryOrders, setDeliveryOrders] = useState<DeliveryOrder[]>([])
    const [creditPayments, setCreditPayments] = useState<CreditPayment[]>([])
    const [fiscalDocuments, setFiscalDocuments] = useState<FiscalDocument[]>([])
    const [adjustmentNotes, setAdjustmentNotes] = useState<AdjustmentNote[]>([])

    const { openedModal } = useUIState()

    const { saleFileName, hoardFileName, operationReceiptFileName, creditPaymentReceiptFileName, fiscalDocumentFileName, hoardStatusFileName, adjustmentNoteFileName } = fileHelpers
    const { describeFiscalDocument } = fiscalDocumentHelpers
    const { describe } = useDescribeHooks()
    const { pricesListFileName } = useHoardHooks()
    const { formatDate } = useDataTypesHooks()
    const downloadHoardPricesListCSV = useDownloadHoardPricesListCSV()

    const deliveryLoaders = useDeliveryLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const creditPaymentLoaders = useCreditPaymentLoaders()
    const fiscalDocumentLoaders = useFiscalDocumentLoaders()
    const adjustmentNoteLoaders = useAdjustmentNoteLoaders()

    const loadCreditPayments = async () => {
        const { loadCustomerCreditPayments } = creditPaymentLoaders()
        const loadedCreditPayments = await loadCustomerCreditPayments(saleOperation!.customerId)
        return loadedCreditPayments.filter(creditPayment => creditPayment.operationItems.some(operationItem => operationItem.operationId === saleOperation!.id))
    }

    const loadFiscalDocuments = async () => {
        const { loadOperationFiscalDocuments } = fiscalDocumentLoaders()
        // Lectura fresca (no optimizada): el comprobante puede haberse autorizado hace instantes y la copia del store puede quedar vieja.
        const loadedFiscalDocuments = await loadOperationFiscalDocuments(saleOperation!.id, false)
        return loadedFiscalDocuments.filter(fiscalDocument => fiscalDocument.pdfUrl)
    }

    // Se recargan los archivos cada vez que se abre el modal: los comprobantes pueden haberse autorizado
    // (autorefresco) después de montar la página y la lista quedaría vieja hasta refrescar el browser.
    useEffect(() => {
        if (saleOperation && openedModal === ModalId.DOWNLOAD_FILES) {
            deliveryLoaders().loadOperationDeliveries(saleOperation.id).then(setDeliveries)
            deliveryOrderLoaders().loadOperationDeliveryOrders(saleOperation.id).then(setDeliveryOrders)
            loadCreditPayments().then(setCreditPayments)
            loadFiscalDocuments().then(setFiscalDocuments)
            adjustmentNoteLoaders().loadOperationAdjustmentNotes(saleOperation.id).then(setAdjustmentNotes)
        }
    }, [saleOperation, openedModal])

    const isSale = useMemo(() => table === TableName.SALE_ORDERS, [table])
    const isCreditPayment = useMemo(() => paymentTypeLoaders().findPaymentType(saleOperation?.paymentTypeId)?.category === PaymentCategory.CREDIT_ACCOUNT, [saleOperation?.paymentTypeId])
    const deliveryNotesFileOptions = DeliveryNotesFileOptions({ deliveries, deliveryOrders })

    const fileOptions = useMemo(() => {
        if (!saleOperation) return []
        
        const options: FileOption[] = []

        const orderFileName = isSale ? saleFileName(saleOperation.code) : hoardFileName(saleOperation.code)
        const orderId = 'order'
        options.push({
            id: `${orderId}-option`,
            label: `Orden de ${describe(table, saleOperation)}`,
            fileName: orderFileName,
            pdf: {
                id: orderId,
                component: isSale ?
                    <SalePDF id={orderId} sale={saleOperation as SaleOrder} /> :
                    <HoardPDF id={orderId} hoard={saleOperation as HoardOrder} />
            }
        })

        if (!isSale) {
            const hoard = saleOperation as HoardOrder
            if (hoard.type === HoardType.FINANCIAL) {
                const statusId = 'hoard-status'
                options.push({
                    id: `${statusId}-option`,
                    label: 'Estado de Acopio',
                    fileName: hoardStatusFileName(hoard.code),
                    pdf: {
                        id: statusId,
                        component: <HoardStatusPDF id={statusId} hoard={hoard} />
                    }
                })
                options.push({
                    id: 'prices-list-option',
                    label: 'Lista de Precios',
                    fileName: `${pricesListFileName(hoard)}.xlsx`,
                    csv: {
                        download: () => downloadHoardPricesListCSV(hoard.id)
                    }
                })
            }
        }

        if ([PaymentStatus.PAYED, PaymentStatus.PARTIALLY_PAYED].includes(saleOperation.paymentStatus)) {
            if (isCreditPayment) {
                creditPayments.forEach(creditPayment => {
                    const receiptId = `receipt-${creditPayment.id}`
                    options.push({
                        id: `${receiptId}-option`,
                        label: `Recibo de Pago (${describe(TableName.CREDIT_PAYMENTS, creditPayment)})`,
                        fileName: creditPaymentReceiptFileName(creditPayment.code),
                        pdf: {
                            id: receiptId,
                            component: <PaymentReceiptPDF id={receiptId} creditPayment={creditPayment} />
                        }
                    })
                })
            } else {
                const receiptId = 'receipt'
                options.push({
                    id: `${receiptId}-option`,
                    label: 'Recibo de Pago',
                    fileName: operationReceiptFileName(saleOperation),
                    pdf: {
                        id: receiptId,
                        component: <PaymentReceiptPDF id={receiptId} operation={saleOperation} />
                    }
                })
            }
        }
        
        saleOperation.paymentItems
            .filter(paymentItem => paymentItem.receiptUrl)
            .sort((paymentItem_A, paymentItem_B) => paymentItem_A.receiptUrl!.localeCompare(paymentItem_B.receiptUrl!))
            .forEach((paymentItem, index) => {
                const { findPaymentType } = paymentTypeLoaders()
                const paymentType = findPaymentType(paymentItem.paymentTypeId)
                if (!paymentType) return
                options.push({
                    id: `receipt-${paymentType.id}-${index}-option`,
                    label: `Comprobante de Pago ${index + 1} (${paymentType.name})`,
                    fileName: fileNameFromUrl(paymentItem.receiptUrl)!,
                    url: paymentItem.receiptUrl
                })
            })

        fiscalDocuments
            .filter(fiscalDocument =>
                fiscalDocument.status === FiscalDocumentStatus.AUTHORIZED &&
                !fiscalDocument.annulledAt &&
                !fiscalDocument.annulmentInFlight
            )
            .sort((fiscalDocumentA, fiscalDocumentB) => fiscalDocumentA.issueDate.localeCompare(fiscalDocumentB.issueDate))
            .forEach(fiscalDocument => {
                options.push({
                    id: `fiscal-document-${fiscalDocument.id}-option`,
                    label: `${describeFiscalDocument(fiscalDocument)} - ${formatDate(new Date(fiscalDocument.issueDate))}`,
                    fileName: fiscalDocumentFileName(fiscalDocument),
                    webUrl: fiscalDocument.pdfUrl
                })
            })

        adjustmentNotes
            .filter(adjustmentNote => !shouldHideAdjustmentNote(adjustmentNote))
            .sort((adjustmentNoteA, adjustmentNoteB) => adjustmentNoteA.date.localeCompare(adjustmentNoteB.date))
            .forEach(adjustmentNote => {
                const adjustmentNoteId = `adjustment-note-${adjustmentNote.id}`
                options.push({
                    id: `${adjustmentNoteId}-option`,
                    label: `${describe(TableName.ADJUSTMENT_NOTES, adjustmentNote)} - ${formatDate(new Date(adjustmentNote.date))}`,
                    fileName: adjustmentNoteFileName(adjustmentNote.code, saleOperation.code),
                    pdf: {
                        id: adjustmentNoteId,
                        component: <AdjustmentNotePDF id={adjustmentNoteId} adjustmentNote={adjustmentNote} operation={saleOperation} />
                    }
                })
            })
        
        if (deliveryNotesFileOptions.length > 0) {
            options.push(...deliveryNotesFileOptions)
        }

        return options
    }, [saleOperation, fiscalDocuments, deliveryNotesFileOptions, isCreditPayment, creditPayments, isSale])
    
    return <DownloadFilesModal fileOptions={fileOptions} />
}
